import { useMemo } from 'react';

import {
  type GmfLinkedAccount,
  useGmfProviderAccount,
} from '~/common/hooks/useGmfProviderAccount';
import { useOrgId } from '~/common/hooks/useOrgId';
import { GmfProviderAccountStatus } from '~/common/models/gmf.model';

type GmfLinkStatus = {
  /**
   * `undefined` when the organization has no GMF provider-account row. That is
   * a different answer from `DISABLED`, though both mean the organization is
   * not linked today.
   */
  status?: GmfProviderAccountStatus;
  /** Account fields from the enrollment, when the read returned them. */
  account?: GmfLinkedAccount;
  /** When the link completed, as an ISO-8601 instant. */
  linkedDate?: string;
  /** True only for `ACTIVE`. */
  isGmfLinked: boolean;
  isLoading: boolean;
  /** True while the enrollment read is in flight, including refetch. */
  isFetching: boolean;
  isError: boolean;
  /** Fetches the enrollment again. */
  refetch: () => void;
};

/**
 * GMF link state for the organization in context, read once for every surface
 * that branches on it: the GM Financial card, Tax ID and primary contact
 * locking, the Fleetio card gate, the support form topic gate, checkout, and
 * the billing surfaces.
 *
 * Returns the status rather than a boolean because callers need different
 * answers. Surfaces that only render for a completed link should use
 * `isGmfLinked`; the GM Financial card branches on `status` to tell an
 * unlinked organization apart from one awaiting customer terms.
 *
 * Two values named `status` reach the frontend. This is the provider-account
 * one. The nested `gmfOrgDetails.status` is GMF's own Active or Terminated
 * value for the business and says nothing about whether we linked it.
 * An explicit organization id overrides the organization in context. An empty
 * id skips the read.
 * @param organizationsId Organization to read. Defaults to the organization in context.
 * @returns The link status, its account fields and link date, a derived `isGmfLinked`, and request state.
 */
export const useGmfLinkStatus = (organizationsId?: string): GmfLinkStatus => {
  const contextOrganizationId = useOrgId();
  const {
    status,
    account,
    linkedDate,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGmfProviderAccount(organizationsId ?? contextOrganizationId);

  return useMemo(
    () => ({
      status,
      account,
      linkedDate,
      isGmfLinked: status === GmfProviderAccountStatus.ACTIVE,
      isLoading,
      isFetching,
      isError,
      refetch,
    }),
    [status, account, linkedDate, isLoading, isFetching, isError, refetch]
  );
};
